import { useState } from "react";
import { useTranslation } from "react-i18next";
import PerformanceTrackingTile from "../../../../common/components/PerformanceTrackingTile/PerformanceTrackingTile";
import { YTDQMonthModal } from "../../../YTDQMonthModal/YTDQMonthModal";
import { getPrevYearShort, getSelectedPerformanceYearData } from "../../../../common/util/period";
import { track } from "../../../../common/util/tealium";
import TEALIUM from "../../../../common/enums/tealium-enums";
import defaultContent from "../../../../content/leadershipSeminar";
import type { LSPathQCountRequirement } from "../../../../services/LeadershipSeminar/leadershipSeminarAPI.types";
import "./LSQMonthsTile.scss";

export interface LSQMonthsTileProps {
  requirement: LSPathQCountRequirement;
  selectedPeriod: number;
}

/** Minimum qualified months required by the Platinum special-condition path. */
const MIN_Q_MONTHS = 9;

type PotentialColor = "green" | "yellow" | "red";

/**
 * Potential Q Months container color (Req 10.11-10.14):
 * - met OR Potential > 9  -> green
 * - Potential === 9       -> yellow
 * - Potential < 9         -> red
 */
const getPotentialColor = (met: boolean, potentialQMonth: number): PotentialColor => {
  if (met || potentialQMonth > MIN_Q_MONTHS) {
    return "green";
  }
  if (potentialQMonth === MIN_Q_MONTHS) {
    return "yellow";
  }
  return "red";
};

const POTENTIAL_ARIA: Record<
  PotentialColor,
  { key: string; fallbackKey: "potentialAriaGreen" | "potentialAriaYellow" | "potentialAriaRed" }
> = {
  green: { key: "pathView.qCountTile.potentialAriaGreen", fallbackKey: "potentialAriaGreen" },
  yellow: { key: "pathView.qCountTile.potentialAriaYellow", fallbackKey: "potentialAriaYellow" },
  red: { key: "pathView.qCountTile.potentialAriaRed", fallbackKey: "potentialAriaRed" },
};

export const LSQMonthsTile = ({ requirement, selectedPeriod }: LSQMonthsTileProps) => {
  const { t } = useTranslation(["leadershipSeminar"]);
  const period = String(selectedPeriod);

  const [isBaselineModalOpen, setIsBaselineModalOpen] = useState(false);
  const [isYtdModalOpen, setIsYtdModalOpen] = useState(false);

  const { selectedPYStartPeriod: currentPYStartPeriod } = getSelectedPerformanceYearData(period);
  const { selectedPYStartPeriod: previousPYStartPeriod } = getSelectedPerformanceYearData(period, 1);
  const prevYearShort = getPrevYearShort(period);

  const handleOpenBaselineModal = () => {
    setIsBaselineModalOpen(true);
    track(
      TEALIUM.U_TAG_METHOD.LINK,
      TEALIUM.EVENT_NAME.CLICK_ACTION,
      TEALIUM.SECTION.Q_MONTHS_TILE,
      TEALIUM.CATEGORY.LEADERSHIP_SEMINAR,
      "",
      "open_baseline_Q_months_modal",
    );
  };

  const handleOpenYtdModal = () => {
    setIsYtdModalOpen(true);
    track(
      TEALIUM.U_TAG_METHOD.LINK,
      TEALIUM.EVENT_NAME.CLICK_ACTION,
      TEALIUM.SECTION.Q_MONTHS_TILE,
      TEALIUM.CATEGORY.LEADERSHIP_SEMINAR,
      "",
      "open_current_Q_months_modal",
    );
  };

  const qc = defaultContent.pathView.qCountTile;
  const potentialColor = getPotentialColor(requirement.Met, requirement.PotentialQMonth);
  const potentialAria = POTENTIAL_ARIA[potentialColor];
  const potentialAriaText = String(t(potentialAria.key, qc[potentialAria.fallbackKey]));

  return (
    <div className="LSQMonthsTile">
      <PerformanceTrackingTile
        met={requirement.Met}
        title={String(t("pathView.qCountTile.title", qc.title, { target: requirement.Target }))}
        sections={[
          {
            title: String(t("pathView.qCountTile.pyBaseline", qc.pyBaseline, { perfYear: prevYearShort })),
            value: requirement.Baseline,
            showIcon: true,
            iconAriaLabel: String(t("pathView.qCountTile.viewBaselineTable", qc.viewBaselineTable)),
            handleBaselineIconTableClick: handleOpenBaselineModal,
          },
          {
            title: String(t("pathView.qCountTile.currentYtdQMonths", qc.currentYtdQMonths)),
            value: requirement.YTDQCount,
            showIcon: true,
            iconAriaLabel: String(t("pathView.qCountTile.viewYtdTable", qc.viewYtdTable)),
            handleIconTableClick: handleOpenYtdModal,
          },
          {
            title: String(t("pathView.qCountTile.potentialQMonths", qc.potentialQMonths)),
            value: (
              <>
                {requirement.PotentialQMonth}
                <span className="sr-only">{potentialAriaText}</span>
              </>
            ),
            className: potentialColor,
          },
        ]}
      />
      <YTDQMonthModal
        isModalOpen={isBaselineModalOpen}
        onClose={() => setIsBaselineModalOpen(false)}
        header="previousYtd"
        period={previousPYStartPeriod}
      />
      <YTDQMonthModal
        isModalOpen={isYtdModalOpen}
        onClose={() => setIsYtdModalOpen(false)}
        header="currentYtd"
        period={currentPYStartPeriod}
      />
    </div>
  );
};

export default LSQMonthsTile;
